import {
  Download,
  Layers,
  LayoutGrid,
  List as ListIcon,
  Plus,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  LEAVE_STATUS_LABEL,
  LEAVE_STATUSES,
  LEAVE_TYPE_LABEL,
  type LeaveStatus,
} from '@/features/hrm/time-off/constants';
import type { TimeOff } from '@/features/hrm/time-off';
import { TIMEOFF_BASE } from '../api';
import { CabinetEmptyState } from '../components/listChrome';
import { CabinetTimeOffForm } from '../components/CabinetTimeOffForm';
import {
  useCabinetTimeOff,
  useCancelCabinetTimeOff,
  useCreateCabinetTimeOff,
  useUpdateCabinetTimeOff,
} from '../queries';
import { useTimeOffUi, type TimeOffViewMode } from '../store/timeOffViewPrefs';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type TimeOffOrderingColumn = 'type' | 'status' | 'start_date' | 'end_date' | 'days';

const VIEW_MODES: readonly ViewModeOption<TimeOffViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

const fmtDate = (d: string) => new Date(d).toLocaleDateString();

function statusVariant(s: LeaveStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'approved') return 'default';
  if (s === 'requested') return 'secondary';
  if (s === 'rejected' || s === 'cancelled') return 'destructive';
  return 'outline';
}

type Panel = { mode: 'create' } | { mode: 'edit'; row: TimeOff } | null;

export function CabinetTimeOffPage() {
  const view = useTimeOffUi(s => s.view);
  const setView = useTimeOffUi(s => s.setView);
  const ordering = useTimeOffUi(s => s.ordering);
  const setOrdering = useTimeOffUi(s => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);
  const [panel, setPanel] = useState<Panel>(null);

  const { data, isPending, isError, error, isFetching } = useCabinetTimeOff({
    page,
    search: search || undefined,
    ordering,
  });
  const create = useCreateCabinetTimeOff();
  const update = useUpdateCabinetTimeOff();
  const cancel = useCancelCabinetTimeOff();

  const rows = data?.results ?? [];

  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="relative max-w-sm flex-1">
        <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
        <Input
          value={searchInput}
          onChange={e => {
            setPage(1);
            setSearchInput(e.target.value);
          }}
          placeholder="Search reason…"
          className="pl-8"
          aria-label="Search time-off requests"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="icon"
          variant="outline"
          onClick={() => {
            void exportCsv(TIMEOFF_BASE, {
              search: search || undefined,
              ordering,
              export: 'csv',
            });
          }}
          aria-label="Export my time off as CSV"
          title="Export my time off as CSV"
        >
          <Download className="size-4" />
        </Button>
        <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Time off</h1>
        {panel === null ? (
          <Button onClick={() => setPanel({ mode: 'create' })}>
            <Plus /> Request time off
          </Button>
        ) : null}
      </div>

      {panel !== null ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {panel.mode === 'create' ? 'Request time off' : 'Edit request'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CabinetTimeOffForm
              submitLabel={panel.mode === 'create' ? 'Submit request' : 'Save changes'}
              defaultValues={
                panel.mode === 'edit'
                  ? {
                      type: panel.row.type,
                      start_date: panel.row.start_date,
                      end_date: panel.row.end_date,
                      days: panel.row.days,
                      reason: panel.row.reason,
                    }
                  : undefined
              }
              onSubmit={async input => {
                if (panel.mode === 'create') await create.mutateAsync(input);
                else await update.mutateAsync({ publicId: panel.row.public_id, input });
                setPanel(null);
              }}
              onCancel={() => setPanel(null)}
            />
          </CardContent>
        </Card>
      ) : null}

      {toolbar}

      {isError ? (
        <p className="text-destructive text-sm">{(error as { message?: string })?.message}</p>
      ) : isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : view === 'table' ? (
        <TimeOffTable
          rows={rows}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onEdit={r => setPanel({ mode: 'edit', row: r })}
          onCancel={r => cancel.mutate(r.public_id)}
          cancelPending={cancel.isPending}
        />
      ) : view === 'list' ? (
        <TimeOffList rows={rows} />
      ) : view === 'cards' ? (
        <TimeOffCards rows={rows} />
      ) : (
        <TimeOffGroupedByStatus rows={rows} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} request{data.count === 1 ? '' : 's'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface TimeOffTableProps {
  rows: TimeOff[];
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  onEdit: (r: TimeOff) => void;
  onCancel: (r: TimeOff) => void;
  cancelPending: boolean;
}

function TimeOffTable({
  rows,
  ordering,
  onOrderingChange,
  onEdit,
  onCancel,
  cancelPending,
}: TimeOffTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<TimeOffOrderingColumn>
              column="type"
              value={ordering}
              onChange={onOrderingChange}
            >
              Type
            </SortableTableHead>
            <SortableTableHead<TimeOffOrderingColumn>
              column="start_date"
              value={ordering}
              onChange={onOrderingChange}
            >
              Dates
            </SortableTableHead>
            <SortableTableHead<TimeOffOrderingColumn>
              column="days"
              value={ordering}
              onChange={onOrderingChange}
              className="text-right"
            >
              Days
            </SortableTableHead>
            <SortableTableHead<TimeOffOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                No time-off requests yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map(r => {
              const pending = r.status === 'requested';
              return (
                <TableRow key={r.id}>
                  <TableCell>{LEAVE_TYPE_LABEL[r.type]}</TableCell>
                  <TableCell>
                    {fmtDate(r.start_date)} – {fmtDate(r.end_date)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{r.days}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(r.status)}>{LEAVE_STATUS_LABEL[r.status]}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {pending ? (
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => onEdit(r)}>
                          Edit
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              Cancel
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel this request?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This withdraws your pending time-off request.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onCancel(r)}
                                disabled={cancelPending}
                              >
                                Cancel request
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function TimeOffList({ rows }: { rows: TimeOff[] }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map(r => (
        <li key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 truncate font-semibold">{LEAVE_TYPE_LABEL[r.type]}</span>
          <span className="text-muted-foreground hidden sm:inline">
            {fmtDate(r.start_date)} – {fmtDate(r.end_date)}
          </span>
          <span className="tabular-nums">{r.days}d</span>
          <Badge variant={statusVariant(r.status)} className="shrink-0">
            {LEAVE_STATUS_LABEL[r.status]}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

function TimeOffCards({ rows }: { rows: TimeOff[] }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map(r => (
        <Card key={r.id}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 text-base">
              {LEAVE_TYPE_LABEL[r.type]}
              <Badge variant={statusVariant(r.status)}>{LEAVE_STATUS_LABEL[r.status]}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
            <span>
              {fmtDate(r.start_date)} – {fmtDate(r.end_date)}
            </span>
            <span className="ml-auto tabular-nums">{r.days} days</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TimeOffGroupedByStatus({ rows }: { rows: TimeOff[] }) {
  const groups = useMemo(() => {
    return LEAVE_STATUSES.map(s => ({
      key: s.value,
      label: s.label,
      bucket: rows.filter(r => r.status === s.value),
    })).filter(g => g.bucket.length > 0);
  }, [rows]);

  if (rows.length === 0) return <EmptyText />;

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => (
        <section key={key} aria-labelledby={`time-off-status-${key}`}>
          <h2
            id={`time-off-status-${key}`}
            className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
          >
            {label}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <TimeOffCards rows={bucket} />
        </section>
      ))}
    </div>
  );
}

function EmptyText() {
  return <CabinetEmptyState>No time-off requests yet.</CabinetEmptyState>;
}
